import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, Badge, Field, Loading, Row, SelectField, TextField, dayLabel, dollars, errText, formValues, longDate, propName, timeLabel, todayPT, useHeaders, usePortal, useSubmit } from './kit';

export const SERVICES = ['Standard Home Clean', 'Signature Deep Reset™', 'Airbnb Turnover', 'Commercial Cleaning', 'Move-In / Move-Out Cleaning'];
export const visitState = (v) => (v.outcome_hold ? 'Under team review' : v.status === 'Canceled' ? 'Canceled' : v.status);
export const isPast = (v) => ['Completed', 'Canceled'].includes(v.status) || new Date(v.end_time || v.start_time) < new Date();

const submitReview = async (headers, body) => (await axios.post(`${API}/api/review-items`, body, { headers })).data;

export const ChangeForm = ({ visit, property, onDone }) => {
  const headers = useHeaders();
  const { toast, closeDrawer } = usePortal();
  const [busy, run] = useSubmit();
  const [type, setType] = useState('Reschedule');
  const submit = (e) => {
    e.preventDefault();
    const f = formValues(e);
    run(async (rid) => {
      try {
        await submitReview(headers, { kind: 'scheduling_request', appointment_id: visit.id, client_request_id: rid, requested_date: type === 'Reschedule' ? f.date : null,
          title: `${type === 'Reschedule' ? 'Reschedule' : 'Cancel'}: ${visit.service_name || 'clean'} on ${dayLabel(visit.start_time)}`,
          details: [type === 'Reschedule' ? `Preferred date: ${longDate(f.date)} · Preferred time: ${f.time}` : 'Asked to cancel this visit', f.note].join('\n') });
        toast('Request sent. Your appointment stays confirmed until your team confirms a change.');
        closeDrawer(); onDone?.();
      } catch (err) { toast(errText(err)); }
    });
  };
  return (
    <>
      <p className="muted">{visit.service_name} · {longDate(visit.start_time)} · {timeLabel(visit.start_time)} PT · {propName(property)}</p>
      <form onSubmit={submit} data-testid="change-form">
        <SelectField label="What would you like to request?" name="type" value={type} onChange={(e) => setType(e.target.value)} options={[['Reschedule', 'Reschedule'], ['Cancel this visit', 'Cancel this visit']]} />
        {type === 'Reschedule' && <Field label="Preferred new date (for rescheduling)" name="date" type="date" min={todayPT()} required />}
        {type === 'Reschedule' && <SelectField label="Preferred time" name="time" options={[['Flexible', 'Flexible'], ['Morning', 'Morning'], ['Afternoon', 'Afternoon']]} />}
        <TextField label="Tell us what you need" name="note" required maxLength={1500} />
        <div className="notice warm">This is a request. Your current appointment stays confirmed until the team reviews it. Any applicable terms will be discussed before a change is finalized.</div>
        <button className="primary" type="submit" disabled={busy} data-testid="change-submit">{busy ? 'Sending…' : 'Send request'}</button>
      </form>
    </>
  );
};

export const NewCleanForm = ({ properties, defaultProperty, onDone }) => {
  const headers = useHeaders();
  const { toast, closeDrawer } = usePortal();
  const [busy, run] = useSubmit();
  const [service, setService] = useState(SERVICES[0]);
  const submit = (e) => {
    e.preventDefault();
    const f = formValues(e);
    const svc = service === SERVICES[4] ? `${f.move} Cleaning` : service;
    run(async (rid) => {
      try {
        await submitReview(headers, { kind: 'new_clean_request', address_id: f.property || null, requested_date: f.date, client_request_id: rid,
          title: `New clean request: ${svc}`, details: [`Preferred date: ${longDate(f.date)} · Preferred time: ${f.time}`, f.note].filter(Boolean).join('\n') });
        toast('Request sent. Your team will confirm availability, scope and price.');
        closeDrawer(); onDone?.();
      } catch (err) { toast(errText(err)); }
    });
  };
  return (
    <form onSubmit={submit} data-testid="new-clean-form">
      <SelectField label="Service" name="service" value={service} onChange={(e) => setService(e.target.value)} options={SERVICES.map((s) => [s, s])} />
      {service === SERVICES[4] && <SelectField label="Move service" name="move" options={[['Move-In', 'Move-In'], ['Move-Out', 'Move-Out']]} />}
      {properties.length > 0 && <SelectField label="Property" name="property" defaultValue={defaultProperty || properties[0].id} options={properties.map((p) => [p.id, propName(p)])} />}
      <Field label="Preferred date" name="date" type="date" min={todayPT()} required />
      <SelectField label="Preferred time" name="time" options={[['Flexible', 'Flexible'], ['Morning', 'Morning'], ['Afternoon', 'Afternoon']]} />
      <TextField label="Anything your team should know?" name="note" maxLength={1500} />
      <div className="notice">Your team confirms availability, scope and price. This does not create a confirmed booking or calculate a quote.</div>
      <button className="primary" type="submit" disabled={busy} data-testid="new-clean-submit">{busy ? 'Sending…' : 'Send request'}</button>
    </form>
  );
};

export const ConcernForm = ({ visit, onDone }) => {
  const headers = useHeaders();
  const { toast, closeDrawer } = usePortal();
  const [busy, run] = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    const f = formValues(e);
    run(async (rid) => {
      try {
        await submitReview(headers, { kind: 'service_concern', appointment_id: visit.id, client_request_id: rid, title: `Service concern: ${visit.service_name || 'clean'} on ${dayLabel(visit.start_time)}`, details: f.note });
        toast('Thanks for telling us. Your team will review and respond here.');
        closeDrawer(); onDone?.();
      } catch (err) { toast(errText(err)); }
    });
  };
  return (
    <form onSubmit={submit} data-testid="concern-form">
      <p className="muted">{visit.service_name} · {longDate(visit.start_time)}</p>
      <TextField label="What didn't go right?" name="note" required minLength={3} maxLength={4000} />
      <div className="notice">Your team reviews every concern, replies here and confirms any return visit or adjustment with you.</div>
      <button className="primary" type="submit" disabled={busy} data-testid="concern-submit">{busy ? 'Sending…' : 'Report issue'}</button>
    </form>
  );
};

const Recap = ({ visit, onReport }) => {
  const headers = useHeaders();
  const [r, setR] = useState(null);
  useEffect(() => { axios.get(`${API}/api/portal/appointments/${visit.id}/recap`, { headers }).then((x) => setR(x.data)).catch(() => setR(false)); }, [visit.id, headers]);
  if (r === null) return <Loading />;
  return (
    <section className="notice" data-testid="visit-recap">
      <strong>Completed-clean recap</strong>
      {r && r.published ? (<>
        {r.checklist?.total > 0 && <p>Checklist: {r.checklist.done} of {r.checklist.total} items completed.</p>}
        {r.outcome_label && <p>Visit outcome: {r.outcome_label}</p>}
        {r.client_note && <p style={{ whiteSpace: 'pre-wrap' }}>{r.client_note}</p>}
        {r.photos?.length > 0 && <div className="inline-actions">{r.photos.map((p) => <img key={p.id} src={p.photo_url} alt="Visit" className="message-photo" />)}</div>}
      </>) : <p>Your team hasn’t published a recap for this visit yet.</p>}
      {(r?.concerns || []).map((c) => <p key={c.id} data-testid={`recap-concern-${c.id}`}><b>{c.title}</b> · {c.response ? `Team response: ${c.response}` : 'Awaiting team review'}</p>)}
      <button className="outline" onClick={onReport} data-testid="visit-report-issue">Report an issue</button>
    </section>
  );
};

export const VisitDetail = ({ visit, property, onChanged }) => {
  const navigate = useNavigate();
  const { openDrawer, closeDrawer } = usePortal();
  const go = (to) => { closeDrawer(); navigate(to); };
  const past = isPast(visit);
  return (
    <div data-testid="visit-detail">
      <div className="section-top"><h3>{visit.service_name || 'Your clean'}</h3><Badge>{visitState(visit)}</Badge></div>
      <Row k="Date" v={longDate(visit.start_time)} />
      <Row k="Start" v={`${timeLabel(visit.start_time)} · Pacific time`} />
      <Row k="Property" v={propName(property)} />
      <Row k="Your Guru" v={visit.cleaner_name || 'Being assigned'} />
      <Row k="Agreed price" v={dollars(visit.quoted_price) || 'Confirmed by your team'} testId="visit-price" />
      {property?.cleaning_priorities && <Row k="Priorities" v={property.cleaning_priorities} />}
      {['Completed', 'Service Finished'].includes(visit.status) && <Recap visit={visit} onReport={() => openDrawer({ title: 'Report an issue', content: <ConcernForm visit={visit} onDone={onChanged} /> })} />}
      {!past && !['In Progress', 'Service Finished', 'Completed'].includes(visit.status) && <><div className="notice">Your team reviews the property instructions before your visit.</div>
        <button className="primary" data-testid="visit-request-change" onClick={() => openDrawer({ title: 'Request a change', content: <ChangeForm visit={visit} property={property} onDone={onChanged} /> })}>Request a scheduling change</button></>}
      <div className="inline-actions">
        <button className="text-button" onClick={() => go('/portal/messages')}>Message your team ↗</button>
        <button className="text-button" onClick={() => go(`/portal/properties?property=${visit.address_id}`)}>Property instructions ↗</button>
      </div>
    </div>
  );
};
